/**
 * Local (in-browser) solving.
 *
 * Entry point for the rest of the app: hand it a Build and a GenConfig and it
 * either returns a solved build or says why it cannot. There is no other solver:
 * a decline is reported (`GenService.reportLocalDecline`) and the generation fails.
 *
 * Off unless a kernel is actually deployed. Which dataset that is comes from
 * `/kernel/manifest.json`, published beside the assets it names, rather than
 * from a build-time variable: the two then ship together and cannot disagree.
 * A build-time digest can be set without the assets being present (or the
 * reverse), and both mistakes look identical at runtime — every generation
 * declined, with nothing in the build to say why.
 */
import { toKernelRequest } from './kernelRequest';
import { preparePool, searchInPool, islandCount, WORKER_SCRIPT_UNAVAILABLE } from './pool';
import { resetKernel } from './kernel';
import { rememberWeaponPoolReference } from './weaponPoolReference';
import { devLog } from '../../utils/debugLog';

/** Thrown by `authorize` and not a decline: the caller handles them. */
const isNotADecline = (err) => err?.status === 429 || err?.serverDown === true;

/**
 * The one reason a reload — rather than a shrug — is the answer.
 *
 * Re-exported so the UI can recognise it without reaching into the pool. Every
 * other asset this page fetches is content-addressed and repairs itself by
 * re-reading the manifest; a hashed worker chunk is the exception, since
 * nothing names it but the bundle already running.
 */
export { WORKER_SCRIPT_UNAVAILABLE };

/** Where the published kernel assets and their manifest live. */
const KERNEL_BASE = '/kernel';

let manifestPromise = null;

/**
 * Digest of the deployed dataset, or '' when no kernel was published.
 *
 * Revalidated rather than served from cache: the assets it points at are
 * content-addressed and therefore immutable, so this pointer is the only thing
 * that may change, and a stale one would request a dataset that no longer exists.
 */
async function publishedKernel() {
    if (!manifestPromise) {
        manifestPromise = fetch(`${KERNEL_BASE}/manifest.json`, { cache: 'no-cache' })
            .then((res) => (res.ok ? res.json() : null))
            // `build` is the wasm's own hash; older manifests omit it.
            .then((m) => (m && typeof m.digest === 'string'
                ? { digest: m.digest, build: typeof m.build === 'string' ? m.build : '' }
                : { digest: '', build: '' }))
            // No kernel assets: every generation declines with "no kernel
            // deployed", which is the reason the dashboard then shows.
            .catch(() => ({ digest: '', build: '' }));
    }
    return manifestPromise;
}

/**
 * Forget the published manifest and anything compiled from it.
 *
 * The manifest is read once and memoised for the life of the tab, which is
 * wrong in exactly one situation: a deploy since that read. The assets it named
 * are gone, every fetch for them 404s, and the tab keeps asking for them until
 * someone reloads. Re-reading it costs one small request and is the difference
 * between a page that repairs itself and a page that cannot generate until it
 * is reloaded.
 */
function refreshPublishedKernel() {
    manifestPromise = null;
    resetKernel();
    return publishedKernel();
}

/** Whether this browser could solve locally at all, kernel aside. */
export function isLocalSolveAvailable() {
    return typeof WebAssembly !== 'undefined' && typeof Worker !== 'undefined';
}

/**
 * Try to solve locally.
 *
 * Resolves `{ solved: false, reason }` rather than throwing when the request is
 * not supported or the kernel fails; the caller reports the reason. The one
 * exception is the generation cap: `authorize` throws its typed quota error and
 * this lets it through, so the caller can show the cap-gate rather than a
 * browser that "cannot generate".
 *
 * @param {object} build
 * @param {object} genConfig
 * @param {{ seconds?: number, onProgress?: Function, onStart?: Function,
 *   onSearching?: Function, onFinalize?: Function }} [opts] `seconds` is the
 *   wall time the user asked to wait, and all of it goes to searching.
 *
 *   Three notifications mark the phases, so the caller can change what the UI
 *   *says* without moving the progress bar, which tracks searching alone:
 *   `onStart` once this commits to solving (every return above it is a
 *   decline), `onSearching` when preparing ends and the budget
 *   starts, and `onFinalize` when the search is over and points are allocated.
 *
 *   `onProgress` carries a `preview` — a finalized build — whenever the
 *   incumbent improves, so the caller can show the best-so-far, and a `metrics`
 *   snapshot, so the generation report fills in as the run goes.
 *
 * On success the outcome carries a `report`: the metrics, convergence series,
 * per-island results, top builds and candidate pools the generation summary
 * panel draws. It is decoration — nothing in it can fail a solve.
 */
export async function trySolveLocally(
    build,
    genConfig,
    { seconds = 4, signal, onProgress, onStart, onSearching, onFinalize, authorize } = {},
) {
    if (!isLocalSolveAvailable()) {
        return { solved: false, reason: 'local solve unavailable' };
    }

    // Named kernelBuild: `build` is already the Build being solved.
    const { digest, build: kernelBuild } = await publishedKernel();
    if (!digest) {
        return { solved: false, reason: 'no kernel deployed' };
    }

    const { request, unsupported, shaping } = toKernelRequest(build, genConfig);
    if (unsupported.length > 0) {
        // Anything the kernel genuinely cannot answer. Solving locally anyway
        // would quietly answer a different question from the one asked.
        return { solved: false, reason: `unsupported: ${unsupported.join(', ')}` };
    }
    // Spells and a weapon objective are criteria too, so a request carrying only
    // those has plenty to solve. Testing stat targets alone sent exactly the
    // spells-only case — the one most worth solving — away.
    const hasStatCriteria = Object.keys(request.targets).length > 0
        || Object.keys(request.maximize_targets).length > 0;
    const hasSpellCriteria = (shaping.spells_criteria || []).length > 0
        || shaping.weapon_criteria != null
        || shaping.best_weapon === true;
    if (!hasStatCriteria && !hasSpellCriteria) {
        // Nothing to optimise. The caller declines rather than solving an empty
        // question anywhere — the UI already disables generating in this state.
        return { solved: false, reason: 'no criteria' };
    }

    // "Items légendaires" off. Which items are legendary is a database column
    // the packed dataset does not carry, so the ids can only come from the
    // authorize call below; without one there is no way to honour the setting.
    // Checked before the pool is built: this run cannot happen locally, and
    // preparing islands we are about to discard would cost the user seconds.
    if (shaping.exclude_legendary && typeof authorize !== 'function') {
        return { solved: false, reason: 'legendary exclusion unavailable' };
    }

    // Everything a local run needs, obtained before anything is spent on it:
    // the assets fetched and compiled, and islands that have answered. A pool
    // that cannot be built must not cost the user a generation from their cap.
    let kernel = { digest, build: kernelBuild };
    let pool = null;
    try {
        pool = await preparePool({ digest: kernel.digest, build: kernel.build });
    } catch (err) {
        // The assets this page was built against are gone — what a deploy looks
        // like from inside a tab that was open when it happened. Re-read the
        // pointer and use whatever is published now.
        const fresh = await refreshPublishedKernel();
        const moved = fresh.digest
            && (fresh.digest !== kernel.digest || fresh.build !== kernel.build);
        // Nothing moved, so a second attempt would fail identically. A worker
        // script that no longer exists is exactly this case: no manifest names
        // it, and only a reload can ask for the one that does.
        if (!moved) return { solved: false, reason: err.message || 'local solve failed' };
        kernel = fresh;
        try {
            pool = await preparePool({ digest: kernel.digest, build: kernel.build });
        } catch (retryErr) {
            return { solved: false, reason: retryErr.message || 'local solve failed' };
        }
    }

    try {
        // Preparing the pool is the longest thing that happens before a permit
        // is bought, so stop can land inside it. Buying one for a run the user
        // has already abandoned is the one thing worse than not solving.
        if (signal?.aborted) return { solved: false, reason: 'cancelled' };

        // Solving locally is now certain to be possible, so this is where
        // permission is bought. Asking earlier would spend a generation from the
        // user's cap on runs that are about to decline — a page whose solver
        // chunk has been deployed away, a kernel that will not load.
        if (typeof authorize === 'function') {
            let permit = null;
            try {
                permit = await authorize(shaping);
            } catch (err) {
                // Neither the cap nor an unreachable server is a decline: the
                // caller shows the cap-gate, or says the server is down.
                if (isNotADecline(err)) throw err;
                permit = null;
            }
            if (!permit?.token) return { solved: false, reason: 'not authorized' };

            // Our dataset is a snapshot baked into this frontend image; the winning
            // item ids are resolved later against the live database. If the two have
            // drifted, the kernel optimises stats nobody will ever see and the build
            // it returns is rendered with different numbers — silently, since both
            // halves are internally consistent. The server cannot see our blob, so it
            // publishes the digest the live data packs to and we compare. Unknown
            // (null) means the background pack has not settled yet
            // and is deliberately not a refusal.
            if (permit.dataset_digest && permit.dataset_digest !== kernel.digest) {
                return { solved: false, reason: 'kernel dataset is stale' };
            }

            // Spell, weapon and passive data the browser cannot derive: spell
            // levels, effect lines and item passives all live in the database.
            // Merged here so the kernel scores the question the user actually asked.
            if (Array.isArray(permit.spell_criteria) && permit.spell_criteria.length > 0) {
                request.spell_criteria = permit.spell_criteria;
            }

            // Find the best weapon: the candidates and the scale they are ranked
            // on. Weapon dice are database rows, not packed stats, so the browser
            // cannot build this. Asking for the criterion and not getting one
            // would solve a request with no weapon objective at all and present
            // the result as though it had one — so that declines instead.
            if (shaping.best_weapon) {
                const pool = permit.best_weapon;
                if (!pool || !Array.isArray(pool.entries) || pool.entries.length === 0) {
                    return { solved: false, reason: 'weapon pool unavailable' };
                }
                request.best_weapon = pool;
                // The panel scores the same criterion for display; it needs the
                // pool's scale, and this is the only place it arrives.
                rememberWeaponPoolReference(request.lvl, pool);
            }
            if (Array.isArray(permit.stat_overrides) && permit.stat_overrides.length > 0) {
                request.stat_overrides = permit.stat_overrides;
            }

            // Legendary ids ride along on the permit; they join the blacklist, the
            // one exclusion mechanism the kernel already has. An older server that
            // does not send them would silently return the legendaries the user
            // asked to keep out, so that case declines instead.
            if (shaping.exclude_legendary) {
                if (!Array.isArray(permit.legendary_item_ids)) {
                    return { solved: false, reason: 'legendary exclusion unavailable' };
                }
                request.blacklist = [
                    ...new Set([...request.blacklist, ...permit.legendary_item_ids.map(Number).filter(Number.isFinite)]),
                ];
            }
        }

        // Past every decline, so the caller knows the run is going ahead before
        // preparing begins. The gauge needs that early: preparing 16 islands
        // takes seconds, and the bar must not creep through them.
        if (typeof onStart === 'function') onStart();

        // The budget is the user's time, spent until it runs out. Deriving an
        // eval count instead means assuming a throughput, and any such constant
        // is wrong on some machine — too low returns early with the progress bar
        // part-filled, too high overshoots the limit the user asked for.
        //
        // All of it goes to searching: preparing and finalizing sit outside the
        // budget and outside the gauge, and are reported as their own states.
        devLog('[Gen] request', { seconds, digest: kernel.digest, request });
        const { result, report, islands, ms, searchMs, prepareMs, evals, rounds } = await searchInPool({
            pool,
            request,
            seconds,
            signal,
            onProgress,
            onPrepared: onSearching,
            onFinalize,
        });
        return { solved: true, result, report, islands, ms, searchMs, prepareMs, evals, rounds };
    } catch (err) {
        // The authorize errors above, which this catch would otherwise turn
        // back into the decline they are not.
        if (isNotADecline(err)) throw err;
        return { solved: false, reason: err.message || 'local solve failed' };
    } finally {
        // Refused permits and stale datasets leave a live pool behind too.
        pool.dispose();
    }
}

export { islandCount };
